import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { cloneObject } from '../../shared/utils/object';
import { Timer } from '../../shared/utils/timer';
import { SessionTimerConfig } from './session-timer.model';
import { SessionTimerService } from './session-timer.service';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'session-timer',
  templateUrl: './session-timer.component.html',
})
export class SessionTimerComponent implements OnInit, OnDestroy {
  @Input() config;
  @Input() sessionAge: number;
  @Input() isLoggedIn: boolean;
  @Output() onSessionTimeout: EventEmitter<void> = new EventEmitter<void>();

  timer: { stop: () => void };
  currentTime: number;
  timeDisplay: string;
  isOpen: boolean;
  sessionTimer: SessionTimerConfig;

  private subscription: Subscription = new Subscription();

  constructor(private sessionTimerService: SessionTimerService) {}

  @HostListener('document:keyup')
  @HostListener('document:click')
  @HostListener('document:wheel')
  @HostListener('document:mousemove')
  resetTimer() {
    if (this.currentTime > this.sessionTimer.shownWithSecondsRemaining) {
      this.timer.stop();
      this.countdown(this.sessionAge);
    }
  }

  ngOnInit() {
    const [config, sessionAge, isLoggedIn] = checkInputsForText([
      this.config,
      this.sessionAge,
      this.isLoggedIn,
    ]);

    this.config = config;
    this.sessionAge = sessionAge;
    this.isLoggedIn = isLoggedIn;

    this.initSessionTimer();
    if (this.isLoggedIn) {
      this.countdown(this.sessionAge);
    } else if (this.timer) {
      this.timer.stop();
    }
  }

  initSessionTimer() {
    const fallbackSessionAge = 300;

    this.sessionAge = this.sessionAge || fallbackSessionAge;
    this.sessionTimer = new SessionTimerConfig(this.config);
  }

  countdown(time: number) {
    if (time || time === 0) {
      const msPerSecond = 1000;

      this.timer = new Timer(() => {
        this.createTimer();
      }, msPerSecond);
      this.currentTime = cloneObject(time);
    }
  }

  createTimer() {
    this.currentTime--;

    if (this.currentTime <= 0) {
      this.timer.stop();
      this.timeDisplay = '00:00';
      this.endSession();
    } else {
      const secondsPerHour = 3600;
      const secondsPerMinute = 60;
      const doubleDigits = 10;
      const mins = Math.floor(
        (this.currentTime % secondsPerHour) / secondsPerMinute,
      );
      const secs = Math.floor(this.currentTime % secondsPerMinute);

      this.timeDisplay = `${mins}:${secs < doubleDigits ? '0' : ''}${secs}`;
    }
  }

  extendSession() {
    this.subscription.add(
      this.sessionTimerService.extendSession().subscribe((response) => {
        this.timer.stop();
        this.countdown(this.sessionAge);
        this.isOpen = false;
      }),
    );
  }

  endSession() {
    this.currentTime = 0;
    this.timer.stop();
    this.onSessionTimeout.emit();
  }

  ngOnDestroy() {
    if (this.timer) {
      this.timer.stop();
    }

    this.subscription.unsubscribe();
  }
}
